import {
  forwardRef,
  ForwardRefRenderFunction,
  TextareaHTMLAttributes,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { twJoin } from 'tailwind-merge';

type ElementAttributes = Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'children'
>;

export type InputAreaInputProps = ElementAttributes & {
  maxChar?: number;
};

const InputAreaInputRenderFunction: ForwardRefRenderFunction<
  HTMLTextAreaElement,
  InputAreaInputProps
> = (props, forwardedRef) => {
  const { maxChar = 500, ...attrs } = props;
  const ref = useRef<HTMLTextAreaElement>(null);

  useImperativeHandle<HTMLTextAreaElement | null, HTMLTextAreaElement | null>(
    forwardedRef,
    () => ref.current,
  );

  useEffect(() => {
    ref.current?.setCustomValidity('');
  }, []);

  return (
    <textarea
      {...attrs}
      ref={ref}
      maxLength={maxChar}
      className={twJoin(
        'box-border w-full appearance-none font-[inherit]',
        'block rounded-md border border-stroke-secondary-40 bg-surface-white p-4',
        'text-marine-high-emphasis',
        'focus:outline-none focus-visible:shadow-focus-state',
        'invalid:border-stroke-error-100 invalid:text-error-100 focus-visible:invalid:border-stroke-error-100',
        'disabled:border-transparent disabled:text-marine-medium-emphasis',
        'read-only:border-transparent read-only:text-marine-medium-emphasis',
        'read-only:bg-surface-secondary-7 disabled:bg-surface-secondary-7',
        'resize-none min-h-[120px]',
        attrs.className,
      )}
    />
  );
};

export const InputAreaInput = forwardRef<
  HTMLTextAreaElement,
  InputAreaInputProps
>(InputAreaInputRenderFunction);
